/**
 * JOB HUNTER › MATCHES — /job-hunter/matches
 *
 * The decision inbox: every job the agent proposed for the active candidate,
 * best fit first. Approve prepares the application; Skip (with a reason)
 * teaches the agent; Save parks it. Same decisions as the WhatsApp buttons.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Bookmark, Inbox, Link2, ListFilter, Search, SkipForward, Target } from 'lucide-react';
import { Button } from '@/components/Button';
import { JobHunterShell, useRunFinished } from '@/components/job-hunter/JobHunterShell';
import { MatchCard } from '@/components/job-hunter/MatchCard';
import { ApplicationDrawer } from '@/components/job-hunter/ApplicationDrawer';
import { ImportJobModal } from '@/components/job-hunter/ImportJobModal';
import { Segmented, JhSelect } from '@/components/job-hunter/ui';
import { useJobHunter } from '@/lib/job-hunter/store';
import { countryName, flag, agentHex } from '@/lib/job-hunter/format';
import { jobHunterService, type JhApplication, type JhStatus } from '@/services/job-hunter.service';

type View = 'new' | 'saved' | 'open' | 'skipped';
const VIEW_STATUS: Record<View, string> = { new: 'proposed', saved: 'saved', open: 'proposed,saved', skipped: 'skipped,expired' };

export default function JhMatches() {
  const { profileUid, profile, agents } = useJobHunter();
  const [agentUid, setAgentUid] = useState('');
  const [view, setView] = useState<View>('new');
  const [q, setQ] = useState('');
  const [query, setQuery] = useState('');
  const [minScore, setMinScore] = useState(0);
  const [signal, setSignal] = useState('');
  const [country, setCountry] = useState('');
  const [sort, setSort] = useState<'score' | 'new'>('score');
  const [rows, setRows] = useState<JhApplication[] | null>(null);
  const [counts, setCounts] = useState<Partial<Record<JhStatus, number>>>({});
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState<string | null>(null);
  const [busy, setBusy] = useState<{ uid: string; action: string } | null>(null);
  const [importing, setImporting] = useState(false);

  useEffect(() => { const t = window.setTimeout(() => setQuery(q.trim()), 300); return () => window.clearTimeout(t); }, [q]);
  useEffect(() => { setPage(1); }, [view, query, minScore, signal, country, sort, agentUid, profileUid]);
  useEffect(() => { setAgentUid(''); }, [profileUid]);

  const load = useCallback(async () => {
    if (!profileUid) return;
    try {
      const r = await jobHunterService.applications({
        profileUid, status: VIEW_STATUS[view], q: query || undefined, minScore: minScore || undefined, signal: signal || undefined,
        country: country || undefined, sort: sort === 'score' ? 'score' : undefined, agentUid: agentUid || undefined, page, limit: 30,
      });
      setRows(r.rows); setCounts(r.counts); setTotal(r.total);
    } catch { setRows([]); }
  }, [profileUid, view, query, minScore, signal, country, sort, agentUid, page]);
  useEffect(() => { void load(); }, [load]);
  useRunFinished(load);

  const decide = async (uid: string, action: 'apply' | 'save' | 'skip') => {
    setBusy({ uid, action });
    try {
      await jobHunterService.decide(uid, action);
      toast.success(action === 'apply' ? 'Approved — application prepared' : action === 'save' ? 'Saved for later' : 'Skipped');
      if (action === 'apply') setOpen(uid);
      await load();
    } catch { /* toasted */ } finally { setBusy(null); }
  };

  const countries = useMemo(() => Array.from(new Set<string>((agents ?? []).flatMap((a) => a.targetCountries).concat(rows?.map((r) => r.evaluation.country ?? '').filter(Boolean) ?? []))).sort(), [agents, rows]);
  const pages = Math.max(1, Math.ceil(total / 30));

  return (
    <JobHunterShell section="Matches" description="Jobs the agent proposed — decide here or on WhatsApp"
      actions={<Button size="sm" variant="secondary" iconLeft={<Link2 size={14} />} onClick={() => setImporting(true)}>Add by link</Button>}>
      <div className="jh-toolbar">
        <Segmented<View> value={view} onChange={setView} options={[
          { value: 'new', label: 'New', n: counts.proposed ?? 0, icon: <Target size={13} /> },
          { value: 'saved', label: 'Saved', n: counts.saved ?? 0, icon: <Bookmark size={13} /> },
          { value: 'open', label: 'All open', n: (counts.proposed ?? 0) + (counts.saved ?? 0), icon: <Inbox size={13} /> },
          { value: 'skipped', label: 'Skipped', n: (counts.skipped ?? 0) + (counts.expired ?? 0), icon: <SkipForward size={13} /> },
        ]} />
        <div className="relative min-w-[200px] flex-1">
          <Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
          <input className="jh-input pl-9" placeholder="Search title or employer" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <ListFilter size={15} className="text-text-muted" />
          {(agents?.length ?? 0) > 1 && (
            <JhSelect size="sm" className="w-40" ariaLabel="Agent" value={agentUid} onChange={setAgentUid}
              options={[{ value: '', label: 'All agents' }, ...(agents ?? []).map((a) => ({ value: a.agentUid, label: a.name, icon: <span className="h-2.5 w-2.5 rounded-full" style={{ background: agentHex(a.color) }} /> }))]} />
          )}
          <JhSelect size="sm" className="w-32" ariaLabel="Minimum fit" value={minScore} onChange={setMinScore} options={[0, 60, 70, 80, 85, 90].map((n) => ({ value: n, label: n ? `Fit ≥ ${n}` : 'Any fit' }))} />
          <JhSelect size="sm" className="w-44" ariaLabel="Sponsorship" value={signal} onChange={setSignal} options={[
            { value: '', label: 'Any sponsorship' }, { value: 'explicit', label: 'Visa sponsored' }, { value: 'likely', label: 'Likely sponsor' },
            { value: 'explicit,likely', label: 'Explicit or likely' }, { value: 'none', label: 'Not mentioned' },
          ]} />
          
          <JhSelect size="sm" className="w-44" ariaLabel="Country" value={country} onChange={setCountry} searchPlaceholder="Search country"
            options={[{ value: '', label: 'All countries' }, ...countries.map((c) => ({ value: c, label: countryName(c), icon: <span className="text-[14px] leading-none">{flag(c)}</span>, keywords: c }))]} />
          <JhSelect size="sm" className="w-36" ariaLabel="Sort" value={sort} onChange={setSort} options={[{ value: 'score' as const, label: 'Best fit first' }, { value: 'new' as const, label: 'Newest first' }]} />
        </div>
      </div>

      {rows === null ? (
        <div className="space-y-2.5">{Array.from({ length: 5 }).map((_, i) => <div key={i} className="h-28 animate-pulse rounded-2xl bg-surface-alt" />)}</div>
      ) : rows.length === 0 ? (
        <div className="jh-hero text-center">
          <Target className="mx-auto text-primary" size={28} />
          <div className="mt-3 text-[15px] font-semibold">{view === 'new' ? 'You are all caught up' : 'Nothing here'}</div>
          <p className="jh-muted mx-auto mt-1 max-w-md">
            {view === 'new' ? `New jobs that clear one of ${profile?.name ?? 'the candidate'}'s agents (fit + sponsorship signal) appear here — and on WhatsApp if alerts are on.` : 'Try another filter.'}
          </p>
          <Button className="mt-4" variant="secondary" iconLeft={<Link2 size={14} />} onClick={() => setImporting(true)}>Add a job by link</Button>
        </div>
      ) : (
        <>
          <div className="space-y-2.5">
            {rows.map((a) => <MatchCard key={a.applicationUid} app={a} onOpen={() => setOpen(a.applicationUid)} onDecide={(act) => decide(a.applicationUid, act)} busy={busy?.uid === a.applicationUid ? busy.action : null} />)}
          </div>
          {pages > 1 && (
            <div className="mt-5 flex items-center justify-center gap-2 text-xs">
              <Button size="sm" variant="ghost" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</Button>
              <span className="text-text-muted">Page {page} of {pages} · {total} matches</span>
              <Button size="sm" variant="ghost" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>Next</Button>
            </div>
          )}
        </>
      )}

      <ApplicationDrawer applicationUid={open} onClose={() => setOpen(null)} onChanged={() => void load()} />
      <ImportJobModal open={importing} onClose={() => setImporting(false)} profileUid={profileUid} agentUid={agentUid || undefined} onImported={(a) => { void load(); if (a) setOpen(a.applicationUid); }} />
    </JobHunterShell>
  );
}
